/**
 * URL-based media caching to reduce R2 bandwidth costs
 * Leverages browser's native HTTP cache + our TTL management
 * Based on proven approach from image-generator-monorepo
 */

interface CachedMedia {
  url: string;
  timestamp: number;
  type: 'image' | 'video';
  size?: number;
  userEmail: string;
}

const CACHE_KEY_PREFIX = 'media_cache_';
const CACHE_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days for compliance
const MAX_CACHE_SIZE = 100 * 1024 * 1024; // 100MB max cache size

export class URLMediaCache {
  private static userEmail: string | null = null;

  /**
   * Initialize cache with user context for data isolation
   */
  static initialize(userEmail: string): void {
    this.userEmail = userEmail;
    this.cleanupCache();
  }

  /**
   * Get cached media URL or return original if not cached/expired
   */
  static getCachedMediaUrl(originalUrl: string, type: 'image' | 'video'): string {
    if (!originalUrl || !this.userEmail) return originalUrl;
    
    try {
      const cacheKey = this.getCacheKey(originalUrl);
      const cached = localStorage.getItem(cacheKey);
      
      if (cached) {
        const cachedData: CachedMedia = JSON.parse(cached);
        
        // Verify user isolation
        if (cachedData.userEmail !== this.userEmail) {
          this.removeCachedMedia(originalUrl);
          return originalUrl;
        }
        
        const isExpired = Date.now() - cachedData.timestamp > CACHE_DURATION;
        
        if (!isExpired && cachedData.type === type) {
          console.log(`[URLMediaCache] Cache hit for ${type}:`, originalUrl.slice(0, 50) + '...');
          return cachedData.url;
        } else {
          // Clean up expired or wrong type cache entry
          this.removeCachedMedia(originalUrl);
        }
      }
    } catch (error) {
      console.warn('[URLMediaCache] Error accessing cache:', error);
    }
    
    return originalUrl;
  }

  /**
   * Cache a media URL with metadata (called after successful load)
   */
  static cacheMedia(originalUrl: string, type: 'image' | 'video', size?: number): void {
    if (!originalUrl || !this.userEmail) return;
    
    try {
      // Clean up old cache entries if needed
      this.cleanupCache();
      
      const cacheKey = this.getCacheKey(originalUrl);
      const cachedData: CachedMedia = {
        url: originalUrl,
        timestamp: Date.now(),
        type,
        size,
        userEmail: this.userEmail
      };
      
      localStorage.setItem(cacheKey, JSON.stringify(cachedData));
      console.log(`[URLMediaCache] Cached ${type}:`, originalUrl.slice(0, 50) + '...');
    } catch (error) {
      console.warn('[URLMediaCache] Error caching media:', error);
    }
  }

  /**
   * Remove a specific cached media item
   */
  static removeCachedMedia(originalUrl: string): void {
    if (!originalUrl) return;
    
    try {
      const cacheKey = this.getCacheKey(originalUrl);
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.warn('[URLMediaCache] Error removing cached media:', error);
    }
  }

  /**
   * Clear all cached media for current user
   */
  static clearUserCache(): void {
    if (!this.userEmail) return;
    
    try {
      const keys = Object.keys(localStorage).filter(key => 
        key.startsWith(CACHE_KEY_PREFIX)
      );
      
      keys.forEach(key => {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const cachedData: CachedMedia = JSON.parse(cached);
            if (cachedData.userEmail === this.userEmail) {
              localStorage.removeItem(key);
            }
          }
        } catch {
          // Remove invalid entries
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('[URLMediaCache] Error clearing user cache:', error);
    }
  }

  /**
   * Get cache statistics for current user
   */
  static getCacheStats(): { count: number; totalSize: number; images: number; videos: number } {
    let count = 0;
    let totalSize = 0;
    let images = 0;
    let videos = 0;
    
    if (!this.userEmail) return { count, totalSize, images, videos };
    
    try {
      const keys = Object.keys(localStorage).filter(key => 
        key.startsWith(CACHE_KEY_PREFIX)
      );
      
      keys.forEach(key => {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const cachedData: CachedMedia = JSON.parse(cached);
            if (cachedData.userEmail === this.userEmail) {
              count++;
              totalSize += cachedData.size || 0;
              if (cachedData.type === 'image') images++;
              if (cachedData.type === 'video') videos++;
            }
          }
        } catch {
          // Skip invalid entries
        }
      });
    } catch (error) {
      console.warn('[URLMediaCache] Error getting cache stats:', error);
    }
    
    return { count, totalSize, images, videos };
  }

  /**
   * Clean up expired cache entries and manage cache size
   */
  private static cleanupCache(): void {
    if (!this.userEmail) return;
    
    try {
      const keys = Object.keys(localStorage).filter(key => 
        key.startsWith(CACHE_KEY_PREFIX)
      );
      
      const entries: Array<{ key: string; data: CachedMedia }> = [];
      
      // Collect all valid cache entries for current user
      keys.forEach(key => {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const cachedData: CachedMedia = JSON.parse(cached);
            
            // Remove entries from other users or expired entries
            if (cachedData.userEmail !== this.userEmail) {
              localStorage.removeItem(key);
              return;
            }
            
            const isExpired = Date.now() - cachedData.timestamp > CACHE_DURATION;
            
            if (isExpired) {
              localStorage.removeItem(key);
            } else {
              entries.push({ key, data: cachedData });
            }
          }
        } catch {
          // Remove invalid entries
          localStorage.removeItem(key);
        }
      });
      
      // Check cache size and remove oldest entries if needed
      const totalSize = entries.reduce((sum, entry) => sum + (entry.data.size || 0), 0);
      
      if (totalSize > MAX_CACHE_SIZE) {
        console.log('[URLMediaCache] Cache size exceeded, cleaning up oldest entries');
        
        // Sort by timestamp (oldest first) and remove until under limit
        entries.sort((a, b) => a.data.timestamp - b.data.timestamp);
        
        let currentSize = totalSize;
        for (const entry of entries) {
          if (currentSize <= MAX_CACHE_SIZE * 0.8) break; // Leave some headroom
          
          localStorage.removeItem(entry.key);
          currentSize -= entry.data.size || 0;
        }
      }
    } catch (error) {
      console.warn('[URLMediaCache] Error during cache cleanup:', error);
    }
  }

  /**
   * Generate cache key from URL
   */
  private static getCacheKey(url: string): string {
    // Use URL without query parameters for consistent caching
    const cleanUrl = url.split('?')[0];
    return `${CACHE_KEY_PREFIX}${btoa(cleanUrl).slice(0, 50)}`;
  }
}